import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Camera,
  FileText,
  Clock,
  MessageSquare,
  Star,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Image,
  Palette,
  Package,
  Users,
  Award,
  Zap,
  ArrowRight,
  Sparkles,
  Target,
  BarChart3,
  Heart,
  Lightbulb,
  BookOpen,
  Ruler,
  Tag,
  Truck,
  RefreshCw,
  ThumbsUp,
  Eye
} from 'lucide-react';

export default function LineeGuidaPage() {
  const algorithmFactors = [
    { factor: 'Completezza profilo', weight: 'Alta', icon: Users, description: 'Bio, foto profilo, storia del laboratorio' },
    { factor: 'Qualità immagini', weight: 'Alta', icon: Camera, description: 'Foto nitide, ben illuminate, multiple angolazioni' },
    { factor: 'Descrizioni prodotti', weight: 'Alta', icon: FileText, description: 'Dettagliate, con materiali e dimensioni' },
    { factor: 'Tempo di risposta', weight: 'Media', icon: Clock, description: 'Risposte rapide a messaggi e richieste' },
    { factor: 'Portfolio attivo', weight: 'Media', icon: Image, description: 'Foto delle lavorazioni e progetti completati' },
    { factor: 'Recensioni positive', weight: 'Alta', icon: Star, description: 'Feedback dei clienti soddisfatti' },
    { factor: 'Attività regolare', weight: 'Media', icon: RefreshCw, description: 'Aggiornamenti frequenti, nuovi prodotti' },
    { factor: 'Tasso di conversione', weight: 'Alta', icon: Target, description: 'Ordini completati con successo' },
  ];

  const guidelines = [
    {
      id: 'profile',
      icon: Users,
      color: 'bg-[#008080]',
      title: 'Crea un Profilo Completo',
      subtitle: 'La tua vetrina inizia da qui',
      description: 'Un profilo completo ispira fiducia e aiuta i clienti a conoscerti. Racconta la tua storia e mostra la tua passione.',
      tips: [
        {
          title: 'Foto profilo professionale',
          description: 'Usa una foto di te nel tuo laboratorio o con le tue creazioni. Evita selfie o foto sfocate.',
          do: true
        },
        {
          title: 'Bio coinvolgente',
          description: 'Racconta chi sei, da quanto tempo lavori, cosa ti distingue. Massimo 2-3 paragrafi.',
          do: true
        },
        {
          title: 'Storia del laboratorio',
          description: 'Se hai una storia interessante, raccontala! I clienti amano sapere da dove vengono i prodotti.',
          do: true
        },
        {
          title: 'Aggiungi le certificazioni',
          description: 'Maestro artigiano, corsi specializzati, riconoscimenti: tutto conta!',
          do: true
        },
        {
          title: 'Profilo vuoto o generico',
          description: 'Un profilo senza foto o con descrizioni vaghe non ispira fiducia.',
          do: false
        }
      ]
    },
    {
      id: 'photos',
      icon: Camera,
      color: 'bg-[#DAA520]',
      title: 'Foto di Qualità Professionale',
      subtitle: 'Le immagini vendono più delle parole',
      description: 'Le foto sono il primo contatto con il cliente. Immagini di qualità aumentano drasticamente le conversioni.',
      tips: [
        {
          title: 'Luce naturale',
          description: 'Fotografa vicino a una finestra o all\'aperto. La luce naturale esalta i dettagli.',
          do: true
        },
        {
          title: 'Sfondo neutro e pulito',
          description: 'Sfondo bianco, legno chiaro o tessuto neutro. Niente distrazioni.',
          do: true
        },
        {
          title: 'Multiple angolazioni',
          description: 'Almeno 4-5 foto: frontale, laterale, dettagli, scala (con oggetto di riferimento).',
          do: true
        },
        {
          title: 'Mostra i dettagli',
          description: 'Cuciture, texture, finiture: i dettagli comunicano qualità.',
          do: true
        },
        {
          title: 'Foto del prodotto in uso',
          description: 'Un bracciale indossato, una borsa abbinata: aiuta il cliente a immaginare.',
          do: true
        },
        {
          title: 'Foto sfocate o buie',
          description: 'Immagini di bassa qualità fanno sembrare anche il prodotto migliore un prodotto scadente.',
          do: false
        },
        {
          title: 'Sfondi disordinati',
          description: 'Evita foto con oggetti estranei, disordine o riflessi indesiderati.',
          do: false
        }
      ]
    },
    {
      id: 'descriptions',
      icon: FileText,
      color: 'bg-[#483D8B]',
      title: 'Descrizioni Complete e Dettagliate',
      subtitle: 'Rispondi alle domande prima che vengano fatte',
      description: 'Una buona descrizione riduce le domande, aumenta la fiducia e migliora il posizionamento.',
      tips: [
        {
          title: 'Materiali utilizzati',
          description: 'Specifica esattamente i materiali: "Pelle pieno fiore italiana" non "pelle".',
          do: true
        },
        {
          title: 'Dimensioni precise',
          description: 'Lunghezza, larghezza, altezza, peso. Usa centimetri e grammi.',
          do: true
        },
        {
          title: 'Tempo di realizzazione',
          description: 'Indica i giorni lavorativi necessari per la creazione.',
          do: true
        },
        {
          title: 'Opzioni di personalizzazione',
          description: 'Colori disponibili, incisioni, modifiche possibili: sii specifico.',
          do: true
        },
        {
          title: 'Cura e manutenzione',
          description: 'Come prendersi cura del prodotto nel tempo.',
          do: true
        },
        {
          title: 'Descrizioni di una riga',
          description: '"Bella borsa fatta a mano" non è sufficiente. Sii dettagliato!',
          do: false
        }
      ]
    },
    {
      id: 'portfolio',
      icon: Image,
      color: 'bg-emerald-600',
      title: 'Portfolio delle Lavorazioni',
      subtitle: 'Mostra il dietro le quinte',
      description: 'Il portfolio mostra il tuo processo creativo e la cura che metti in ogni creazione. I clienti adorano vedere come nascono i prodotti.',
      tips: [
        {
          title: 'Foto del processo',
          description: 'Dalla materia prima al prodotto finito: documenta le fasi principali.',
          do: true
        },
        {
          title: 'Il tuo laboratorio',
          description: 'Mostra dove lavori, i tuoi strumenti, il tuo ambiente creativo.',
          do: true
        },
        {
          title: 'Progetti speciali',
          description: 'Lavori su commissione, pezzi unici, collaborazioni: mettili in evidenza.',
          do: true
        },
        {
          title: 'Prima e dopo',
          description: 'Se fai restauri o personalizzazioni, mostra la trasformazione.',
          do: true
        },
        {
          title: 'Portfolio vuoto',
          description: 'Un portfolio vuoto è un\'occasione persa. Anche poche foto fanno la differenza.',
          do: false
        }
      ]
    },
    {
      id: 'response',
      icon: MessageSquare,
      color: 'bg-rose-600',
      title: 'Rispondi Velocemente',
      subtitle: 'La velocità crea fiducia',
      description: 'I clienti si aspettano risposte rapide. Un tempo di risposta breve aumenta le probabilità di conversione e migliora il tuo ranking.',
      tips: [
        {
          title: 'Rispondi entro 24 ore',
          description: 'Idealmente entro poche ore. Il cliente potrebbe rivolgersi altrove.',
          do: true
        },
        {
          title: 'Attiva le notifiche',
          description: 'Ricevi notifiche push o email per non perdere messaggi.',
          do: true
        },
        {
          title: 'Risposte complete',
          description: 'Anticipa le domande successive. Meglio una risposta completa che tre messaggi.',
          do: true
        },
        {
          title: 'Tono professionale ma cordiale',
          description: 'Sii disponibile e gentile, ma anche professionale.',
          do: true
        },
        {
          title: 'Ignorare i messaggi',
          description: 'Anche un "no" è meglio del silenzio. Rispondi sempre.',
          do: false
        },
        {
          title: 'Risposte secche o scortesi',
          description: 'Il tono fa la differenza. Il cliente potrebbe lasciare una recensione.',
          do: false
        }
      ]
    },
    {
      id: 'pricing',
      icon: Tag,
      color: 'bg-orange-600',
      title: 'Prezzi Giusti e Trasparenti',
      subtitle: 'Valorizza il tuo lavoro',
      description: 'Il prezzo comunica valore. Non svendere il tuo lavoro, ma sii trasparente su cosa è incluso.',
      tips: [
        {
          title: 'Calcola il vero costo',
          description: 'Materiali + tempo + competenza + margine. Non dimenticare nulla.',
          do: true
        },
        {
          title: 'Spiega il valore',
          description: 'Perché il tuo prodotto costa così? Racconta il valore artigianale.',
          do: true
        },
        {
          title: 'Spedizione chiara',
          description: 'Indica i costi di spedizione o se sono inclusi nel prezzo.',
          do: true
        },
        {
          title: 'Prezzi coerenti',
          description: 'Prodotti simili dovrebbero avere prezzi simili. Sii coerente.',
          do: true
        },
        {
          title: 'Prezzi troppo bassi',
          description: 'Svalutano il lavoro artigianale e creano sospetti sulla qualità.',
          do: false
        }
      ]
    }
  ];

  const quickWins = [
    { icon: Camera, text: 'Aggiungi almeno 5 foto per prodotto' },
    { icon: FileText, text: 'Scrivi descrizioni di almeno 100 parole' },
    { icon: Clock, text: 'Rispondi ai messaggi entro 24 ore' },
    { icon: Image, text: 'Carica almeno 3 foto nel portfolio' },
    { icon: Users, text: 'Completa al 100% il tuo profilo' },
    { icon: RefreshCw, text: 'Aggiungi o aggiorna prodotti ogni settimana' },
  ];

  return (
    <>
      <Helmet>
        <title>Linee Guida per Artigiani | Come Vendere di Più su Rarifex</title>
        <meta name="description" content="Scopri le linee guida per ottimizzare la tua vetrina su Rarifex. Consigli su foto, descrizioni, portfolio e tempi di risposta per aumentare le vendite." />
        <meta name="keywords" content="linee guida Rarifex, come vendere artigianato, ottimizzare vetrina online, foto prodotti artigianali, descrizioni prodotti" />
        <link rel="canonical" href="https://rarifex.com/linee-guida" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Linee Guida per Artigiani | Rarifex" />
        <meta property="og:description" content="Consigli pratici per ottimizzare la tua vetrina e vendere di più su Rarifex." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rarifex.com/linee-guida" />
        
        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Linee Guida per Artigiani su Rarifex",
            "description": "Guida completa per ottimizzare la tua vetrina e aumentare le vendite su Rarifex",
            "author": {
              "@type": "Organization",
              "name": "Rarifex"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Rarifex",
              "url": "https://rarifex.com"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#008080] via-[#006666] to-[#483D8B] text-white py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/30">
                <BookOpen className="w-3 h-3 mr-1" />
                Guida per Artigiani
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Linee Guida per il Successo
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Segui queste best practice per ottimizzare la tua vetrina, 
                migliorare il posizionamento dei tuoi prodotti e aumentare le vendite.
              </p>
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm">
                <BarChart3 className="w-4 h-4" />
                <span>Questi fattori influenzano il tuo ranking nell'algoritmo di ricerca</span>
              </div>
            </div>
          </div>
        </section>

        {/* Algorithm Factors */}
        <section className="py-12 -mt-8">
          <div className="max-w-5xl mx-auto px-4">
            <Card className="p-6 md:p-8 shadow-xl border-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#008080]/10 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#008080]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Fattori dell'Algoritmo</h2>
                  <p className="text-sm text-slate-500">Cosa influenza il posizionamento dei tuoi prodotti</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {algorithmFactors.map((item, index) => (
                  <div key={index} className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <item.icon className="w-4 h-4 text-[#008080]" />
                      <span className="font-medium text-slate-900 text-sm">{item.factor}</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{item.description}</p>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        item.weight === 'Alta' 
                          ? 'border-emerald-300 text-emerald-700 bg-emerald-50' 
                          : 'border-amber-300 text-amber-700 bg-amber-50'
                      }`}
                    >
                      Importanza: {item.weight}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* Quick Wins */}
        <section className="py-12 bg-[#008080]/5">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-8">
              <Zap className="w-10 h-10 text-[#DAA520] mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Azioni Rapide</h2>
              <p className="text-slate-600">6 cose che puoi fare subito per migliorare</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quickWins.map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                  <div className="w-10 h-10 bg-[#DAA520]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#DAA520]" />
                  </div>
                  <span className="text-slate-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Guidelines Sections */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Guida Completa
              </h2>
              <p className="text-lg text-slate-600">
                Approfondisci ogni aspetto per ottimizzare la tua presenza su Rarifex
              </p>
            </div>

            <div className="space-y-8">
              {guidelines.map((section, sectionIndex) => (
                <Card key={section.id} className="overflow-hidden border-0 shadow-lg" id={section.id}>
                  <div className={`${section.color} p-6 text-white`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <section.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{section.title}</h3>
                        <p className="text-white/80 text-sm">{section.subtitle}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-slate-600 mb-6">{section.description}</p>
                    
                    <div className="space-y-3">
                      {section.tips.map((tip, tipIndex) => (
                        <div 
                          key={tipIndex} 
                          className={`flex gap-3 p-4 rounded-xl ${
                            tip.do 
                              ? 'bg-emerald-50 border border-emerald-100' 
                              : 'bg-red-50 border border-red-100'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                            tip.do ? 'bg-emerald-500' : 'bg-red-500'
                          }`}>
                            {tip.do ? (
                              <CheckCircle className="w-4 h-4 text-white" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <div>
                            <p className={`font-medium ${tip.do ? 'text-emerald-900' : 'text-red-900'}`}>
                              {tip.do ? 'Sì: ' : 'No: '}{tip.title}
                            </p>
                            <p className={`text-sm ${tip.do ? 'text-emerald-700' : 'text-red-700'}`}>
                              {tip.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Summary Card */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <Card className="p-8 md:p-12 text-center border-2 border-[#008080]/20">
              <Award className="w-16 h-16 text-[#DAA520] mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                In Sintesi: La Formula del Successo
              </h2>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <Badge className="bg-[#008080] text-white px-4 py-2 text-sm">
                  <Camera className="w-4 h-4 mr-2" />
                  Foto di qualità
                </Badge>
                <span className="text-2xl text-slate-400">+</span>
                <Badge className="bg-[#DAA520] text-white px-4 py-2 text-sm">
                  <FileText className="w-4 h-4 mr-2" />
                  Descrizioni complete
                </Badge>
                <span className="text-2xl text-slate-400">+</span>
                <Badge className="bg-[#483D8B] text-white px-4 py-2 text-sm">
                  <Clock className="w-4 h-4 mr-2" />
                  Risposte rapide
                </Badge>
                <span className="text-2xl text-slate-400">=</span>
                <Badge className="bg-emerald-500 text-white px-4 py-2 text-sm">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Più vendite
                </Badge>
              </div>
              <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
                L'algoritmo di Rarifex premia gli artigiani che offrono un'esperienza completa e professionale. 
                Seguendo queste linee guida, i tuoi prodotti appariranno più in alto nei risultati di ricerca 
                e avrai maggiori probabilità di convertire i visitatori in clienti.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button size="lg" className="bg-[#008080] hover:bg-[#006666]" asChild>
                  <Link to="/dashboard">
                    Vai alla Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/come-vendere">
                    Come Vendere
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#008080] to-[#483D8B] text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Pronto a Ottimizzare la Tua Vetrina?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Applica queste linee guida e guarda crescere le tue vendite. 
              Se hai bisogno di aiuto, siamo qui per te.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-[#008080] hover:bg-slate-100"
                asChild
              >
                <Link to="/plans">
                  Inizia Ora
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                asChild
              >
                <Link to="/faq">
                  Leggi le FAQ
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
